import type {
	API_CheckCustomerScheduleConflictsRequest,
	API_CheckCustomerScheduleConflictsResponse,
	API_CreateEnrollmentRequest,
	API_ExtendEnrollmentRequest,
	API_ExtendEnrollmentResponse,
	API_GetEnrollmentResponse,
	API_ListEnrollmentsFilters,
	API_ListEnrollmentsResponse,
	API_MutationConfirmation,
	API_PauseEnrollmentRequest,
	API_PauseEnrollmentResponse,
	API_UnpauseEnrollmentRequest,
	API_UnpauseEnrollmentResponse,
	API_UpdateEnrollmentRequest,
	API_UpdateEnrollmentResponse,
	PaginationRequest,
} from "@pilatestime/shared-types";
import {
	type EnrollmentStatus,
	EnrollmentWorkflowType,
} from "@pilatestime/shared-types";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import { parseCapacityExceededError } from "@/utils/capacityError";
import api from "./api_client";

// API functions
export const fetchOrganizationEnrollments = (
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
	statuses?: EnrollmentStatus[],
	expiringSoon?: boolean,
	missingSchedule?: boolean,
	classArchived?: boolean,
): Promise<API_ListEnrollmentsResponse> =>
	api
		.get(`/organizations/${organizationId}/enrollments`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				...paginationParams,
				...(searchTerm && { search: searchTerm }),
				...(statuses &&
					statuses.length > 0 && { statuses: statuses.join(",") }),
				...(expiringSoon && { expiringSoon: true }),
				...(missingSchedule && { missingSchedule: true }),
				...(classArchived && { classArchived: true }),
			},
		})
		.then((res) => res.data);

export const fetchEnrollmentById = (
	token: string,
	organizationId: string,
	enrollmentId: string,
): Promise<API_GetEnrollmentResponse> =>
	api
		.get(`/organizations/${organizationId}/enrollments/${enrollmentId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const fetchCustomerEnrollments = (
	token: string,
	organizationId: string,
	customerId: string,
	paginationParams: PaginationRequest,
	statuses?: EnrollmentStatus[],
): Promise<API_ListEnrollmentsResponse> =>
	api
		.get(`/organizations/${organizationId}/enrollments`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				...paginationParams,
				customerId,
				...(statuses &&
					statuses.length > 0 && { statuses: statuses.join(",") }),
			},
		})
		.then((res) => res.data);

// DEPRECATED: Legacy enrollment creation functions removed
// Use createScheduleEnrollment instead

// Unified schedule-enrollment endpoint
export const createScheduleEnrollment = (
	token: string,
	organizationId: string,
	data: API_CreateEnrollmentRequest,
): Promise<API_MutationConfirmation> =>
	api
		.post(`/organizations/${organizationId}/enrollments`, data, {
			headers: { Authorization: `Bearer ${token}` },
		})
		.then((res) => res.data);

export const updateEnrollment = (
	token: string,
	organizationId: string,
	enrollmentId: string,
	data: API_UpdateEnrollmentRequest,
): Promise<API_UpdateEnrollmentResponse> =>
	api
		.put(`/organizations/${organizationId}/enrollments/${enrollmentId}`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const deleteEnrollment = (
	token: string,
	organizationId: string,
	enrollmentId: string,
): Promise<void> =>
	api
		.delete(`/organizations/${organizationId}/enrollments/${enrollmentId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then(() => undefined);

// Filter params type for enrollments — reuses the shared list-filter contract
// so the frontend and backend stay in sync (single source of truth).
export type EnrollmentFilterParams = Pick<
	API_ListEnrollmentsFilters,
	"statuses" | "expiringSoon" | "missingSchedule" | "classArchived"
>;

// Query options
export const fetchOrganizationEnrollmentsQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
	filterParams?: EnrollmentFilterParams,
) =>
	queryOptions({
		queryKey: [
			"organization_enrollments",
			organizationId,
			userId,
			paginationParams,
			searchTerm ?? "",
			filterParams?.statuses ?? [],
			filterParams?.expiringSoon ?? false,
			filterParams?.missingSchedule ?? false,
			filterParams?.classArchived ?? false,
		],
		queryFn: () =>
			fetchOrganizationEnrollments(
				token,
				organizationId,
				paginationParams,
				searchTerm,
				filterParams?.statuses,
				filterParams?.expiringSoon,
				filterParams?.missingSchedule,
				filterParams?.classArchived,
			),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchEnrollmentByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	enrollmentId: string,
) =>
	queryOptions({
		queryKey: ["enrollment", organizationId, enrollmentId, userId],
		queryFn: () => fetchEnrollmentById(token, organizationId, enrollmentId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchCustomerEnrollmentsQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
	paginationParams: PaginationRequest,
	statuses?: EnrollmentStatus[],
) =>
	queryOptions({
		queryKey: [
			"customer_enrollments",
			organizationId,
			customerId,
			userId,
			paginationParams,
			statuses,
		],
		queryFn: () =>
			fetchCustomerEnrollments(
				token,
				organizationId,
				customerId,
				paginationParams,
				statuses,
			),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useUpdateEnrollment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			enrollmentId,
			data,
			userId: _userId,
		}: {
			token: string;
			organizationId: string;
			enrollmentId: string;
			data: API_UpdateEnrollmentRequest;
			userId: string;
		}) => updateEnrollment(token, organizationId, enrollmentId, data),
		toastMessages: {
			loading: t("enrollments.toasts.updating"),
			success: t("enrollments.toasts.updateSuccess"),
			error: t("enrollments.toasts.updateError"),
		},
		onSuccess: (updatedEnrollment, variables) => {
			try {
				const { organizationId } = updatedEnrollment;
				const customerId = updatedEnrollment.customerId;
				const enrollmentId = updatedEnrollment.id;
				const scheduleIds = updatedEnrollment.schedules?.map(
					(schedule) => schedule.id,
				);

				// 1) Organization enrollments (list page)
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments", organizationId],
				});
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments"],
				});
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments_search", organizationId],
				});

				// 2) Customer enrollments (customer details → enrollments section)
				if (customerId) {
					queryClient.invalidateQueries({
						queryKey: ["customer_enrollments", organizationId, customerId],
					});
				}

				// 3) Schedule enrollments (schedule details → enrollments section)
				if (Array.isArray(scheduleIds)) {
					for (const scheduleId of scheduleIds) {
						queryClient.invalidateQueries({
							queryKey: ["enrollments", "schedule", organizationId, scheduleId],
						});
					}
				}

				// 4) Specific enrollment detail query (edit page)
				queryClient.refetchQueries({
					queryKey: [
						"enrollment",
						organizationId,
						enrollmentId,
						variables.userId,
					],
				});

				// 5) Organization schedules & sessions/attendance (counts and availability)
				queryClient.invalidateQueries({
					queryKey: ["organization_schedules", organizationId],
				});
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions", "list", organizationId],
				});
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions", "monthly", organizationId],
				});
				queryClient.invalidateQueries({
					queryKey: ["attendance", "list"],
				});
			} catch (error) {
				// Log the error but don't re-throw it since the API call already succeeded
				// Re-throwing would cause mutateAsync to fail even though the enrollment update was persisted
				console.error("[useUpdateEnrollment] Error in onSuccess:", error);
			}
		},
	});
};

// Unified schedule-enrollment mutation hook
export const useCreateScheduleEnrollmentMutation = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateEnrollmentRequest;
		}) => createScheduleEnrollment(token, organizationId, data),
		toastMessages: {
			loading: t("enrollments.toasts.creating"),
			success: t("enrollments.toasts.createSuccess"),
			error: t("enrollments.toasts.createError"),
		},
		showErrorToast: false,
		onSuccess: (_data, variables) => {
			console.log("[useCreateScheduleEnrollmentMutation] onSuccess called", {
				_data,
				variables,
			});
			try {
				const { organizationId, data } = variables;
				const customerId = data.data.enrollmentFields.customerId;

				// Invalidate organization enrollment list queries (with and without search)
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments", organizationId],
				});
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments"],
				});
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments_search", organizationId],
				});

				// Invalidate customer enrollments for the affected customer
				if (customerId) {
					queryClient.invalidateQueries({
						queryKey: ["customer_enrollments", organizationId, customerId],
					});
				}

				// Invalidate schedule enrollments for existing schedules used in the enrollment
				if (data.kind === EnrollmentWorkflowType.EXISTING_SCHEDULES) {
					const scheduleIds = data.data.enrollmentFields.scheduleIds ?? [];
					for (const scheduleId of scheduleIds) {
						queryClient.invalidateQueries({
							queryKey: ["enrollments", "schedule", organizationId, scheduleId],
						});
					}
				}

				// Invalidate organization schedules to update enrollment counts
				queryClient.invalidateQueries({
					queryKey: ["organization_schedules", organizationId],
				});
				// Invalidate calendar/attendance queries
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions", "list", organizationId],
				});
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions", "monthly", organizationId],
				});
				queryClient.invalidateQueries({
					queryKey: ["attendance", "list"],
				});
				console.log(
					"[useCreateScheduleEnrollmentMutation] Query invalidation completed successfully",
				);
			} catch (error) {
				// Log the error but don't re-throw it since the API call already succeeded
				// Re-throwing would cause mutateAsync to fail even though the enrollment was created
				console.error(
					"[useCreateScheduleEnrollmentMutation] Error in onSuccess:",
					error,
				);
			}
		},
		onError: (error: AxiosError) => {
			// Handle specific error cases following the established pattern.
			// The capacity-exceeded 409 shares its status with the duplicate-enrollment
			// 409, so narrow by the typed body before falling back to the duplicate message.
			const capacityError = parseCapacityExceededError(error);
			if (capacityError) {
				toast.error(
					t("common.errors.capacityExceededSchedule", {
						current: capacityError.current,
						capacity: capacityError.capacity,
					}),
				);
			} else if (error.response?.status === 409) {
				// Show specific error message for duplicate enrollment
				toast.error(t("enrollments.toasts.createErrorDuplicate"));
			} else {
				// For all other errors, show the generic error message
				// This maintains consistency with the toastMessages.error pattern
				toast.error(t("enrollments.toasts.createError"));
			}
		},
	});
};

// Hook to get enrollments for a specific schedule
export const useEnrollmentsBySchedule = (
	token: string,
	organizationId: string,
	scheduleId: string,
) => {
	return useQuery({
		queryKey: ["enrollments", "schedule", organizationId, scheduleId],
		queryFn: async (): Promise<API_ListEnrollmentsResponse> => {
			const response = await api.get(
				`/organizations/${organizationId}/schedules/${scheduleId}/enrollments`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);
			return response.data;
		},
		enabled: !!token && !!organizationId && !!scheduleId,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

// Pause enrollment API function
export const pauseEnrollment = (
	token: string,
	organizationId: string,
	enrollmentId: string,
	data?: API_PauseEnrollmentRequest,
): Promise<API_PauseEnrollmentResponse> =>
	api
		.post(
			`/organizations/${organizationId}/enrollments/${enrollmentId}/pause`,
			data ?? {},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

// Unpause enrollment API function. `attachScheduleId` re-attaches an active
// schedule as part of the resume (D3) when the enrollment holds no active link.
export const unpauseEnrollment = (
	token: string,
	organizationId: string,
	enrollmentId: string,
	data: API_UnpauseEnrollmentRequest = {},
): Promise<API_UnpauseEnrollmentResponse> =>
	api
		.post(
			`/organizations/${organizationId}/enrollments/${enrollmentId}/unpause`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

// Check customer schedule conflicts
export const checkCustomerScheduleConflicts = (
	token: string,
	organizationId: string,
	customerId: string,
	data: API_CheckCustomerScheduleConflictsRequest,
): Promise<API_CheckCustomerScheduleConflictsResponse> =>
	api
		.post(
			`/organizations/${organizationId}/customers/${customerId}/enrollment-schedule-conflicts`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

// Pause enrollment mutation hook
export const usePauseEnrollment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			enrollmentId,
			data,
		}: {
			token: string;
			organizationId: string;
			enrollmentId: string;
			data?: API_PauseEnrollmentRequest;
			userId: string;
		}) => pauseEnrollment(token, organizationId, enrollmentId, data),
		toastMessages: {
			loading: t("enrollments.toasts.pausing"),
			success: t("enrollments.toasts.pauseSuccess"),
			error: t("enrollments.toasts.pauseError"),
		},
		onSuccess: (_result, variables) => {
			const { organizationId, enrollmentId, userId } = variables;

			// Invalidate enrollment queries
			queryClient.invalidateQueries({
				queryKey: ["organization_enrollments", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_enrollments"],
			});
			queryClient.refetchQueries({
				queryKey: ["enrollment", organizationId, enrollmentId, userId],
			});

			// Invalidate schedule sessions and attendance
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
		},
	});
};

// Extend enrollment API function
export const extendEnrollment = (
	token: string,
	organizationId: string,
	enrollmentId: string,
	data: API_ExtendEnrollmentRequest,
): Promise<API_ExtendEnrollmentResponse> =>
	api
		.post(
			`/organizations/${organizationId}/enrollments/${enrollmentId}/extend`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

// Extend enrollment mutation hook
export const useExtendEnrollment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			enrollmentId,
			data,
		}: {
			token: string;
			organizationId: string;
			enrollmentId: string;
			data: API_ExtendEnrollmentRequest;
			userId: string;
		}) => extendEnrollment(token, organizationId, enrollmentId, data),
		toastMessages: {
			loading: t("enrollments.toasts.extending"),
			success: t("enrollments.toasts.extendSuccess"),
			error: t("enrollments.toasts.extendError"),
		},
		onSuccess: (_result, variables) => {
			const { organizationId, enrollmentId, userId } = variables;

			// Invalidate enrollment queries
			queryClient.invalidateQueries({
				queryKey: ["organization_enrollments", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_enrollments"],
			});
			queryClient.refetchQueries({
				queryKey: ["enrollment", organizationId, enrollmentId, userId],
			});

			// Invalidate schedule sessions and attendance (sessions are regenerated)
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
		},
	});
};

// Unpause enrollment mutation hook
export const useUnpauseEnrollment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			enrollmentId,
			attachScheduleId,
		}: {
			token: string;
			organizationId: string;
			enrollmentId: string;
			userId: string;
			attachScheduleId?: string;
		}) =>
			unpauseEnrollment(token, organizationId, enrollmentId, {
				attachScheduleId,
			}),
		toastMessages: {
			loading: t("enrollments.toasts.unpausing"),
			success: t("enrollments.toasts.unpauseSuccess"),
			error: t("enrollments.toasts.unpauseError"),
		},
		onSuccess: (_result, variables) => {
			const { organizationId, enrollmentId, userId } = variables;

			// Invalidate enrollment queries
			queryClient.invalidateQueries({
				queryKey: ["organization_enrollments", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_enrollments"],
			});
			queryClient.refetchQueries({
				queryKey: ["enrollment", organizationId, enrollmentId, userId],
			});

			// Invalidate schedule sessions and attendance
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
		},
	});
};
